import type { RelatedEntityType, Task } from "@dune/types";
import { RoleEnum } from "@dune/vars";
import { useEffect } from "react";
import { useMe } from "../../api/queries/users.queries";
import { useSelectedTaskId, useTaskActions } from "../../stores/task.store";
import type { TaskDetailsPanelProps } from "../../types/panel.types";
import { getEntityPanelComponent } from "../../utils/panel-registry";
import { usePanel } from "../usePanel";

interface UseTaskSelectionProps {
	entityType: RelatedEntityType;
	entityId: number;
	tasks: Task[];
	customComponent?: React.ComponentType<TaskDetailsPanelProps>;
}

export const useTaskSelection = ({
	entityType,
	entityId,
	tasks,
	customComponent,
}: UseTaskSelectionProps) => {
	const panel = usePanel();
	const selectedTaskId = useSelectedTaskId();
	const { clearTaskSelection } = useTaskActions();
	const { me } = useMe();

	const TaskDetailsComponent =
		customComponent || getEntityPanelComponent(entityType, "TaskDetails");

	useEffect(() => {
		if (!selectedTaskId || !tasks.length) return;

		if (me?.role !== RoleEnum.DUNE) {
			clearTaskSelection();
			return;
		}

		const task = tasks.find((t) => t.id.toString() === selectedTaskId);

		if (!task) {
			return;
		}

		if (TaskDetailsComponent) {
			panel.showCustomContent({
				component: (
					<TaskDetailsComponent
						task={task}
						entityId={entityId}
						entityType={entityType}
					/>
				),
				meta: {
					title: `Tâche #${task.id}`,
					onClose: () => {
						panel.clearPanel();
						clearTaskSelection();
					},
					onBack: () => {
						panel.clearPanel();
						clearTaskSelection();
					},
				},
			});
		} else {
			panel.showTaskDetails({
				task,
				entityId,
				entityType,
			});
		}
	}, [
		selectedTaskId,
		tasks,
		entityId,
		entityType,
		panel,
		TaskDetailsComponent,
		clearTaskSelection,
		me?.role,
	]);

	return {
		selectedTaskId,
		clearTaskSelection,
	};
};
